import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Modal } from '@shared/components/modals/Modal';
import { setLastCommentsRefresh, setLastUpdateProductsPlanDate } from '@store/reducers/contactManagerSlice';
import { EditProductBoxWrapper } from '@styles/CommonStyles';

import {
  DealProduct,
  Deductible,
  IEditProductModal,
  ISelectedRetail,
  Option,
  ProductPlan,
  removedProducts,
  Surcharge,
} from '../interfaces';
import { EditRetailForm } from './EditRetailForm';
import { ProductsTree } from './ProductsTree';

export interface ISelectedRetailDeductible extends ISelectedRetail {
  option: Deductible;
  dealCalculatorLeadProductId?: string;
  dealProductId?: string;
}

export interface ISelectedRetailOption extends ISelectedRetail {
  option: Option;
}

export interface ISelectedRetailSurcharge extends ISelectedRetail {
  option: Surcharge;
}

export interface SelectedRetails {
  deductiblesSelected: Map<string, ISelectedRetailDeductible>;
  optionSelected: Map<string, ISelectedRetailOption>;
  surchargeSelected: Map<string, ISelectedRetailSurcharge>;
  dealsProductsMap: Map<string, DealProduct>;
  removedProducts: Map<string, removedProducts>;
}

export const EditProductModal = ({
  contactAddress,
  editProductPosition,
  setEditProductPosition,
  hideBackdrop,
  customData,
  reloadDeal,
  isVoidEnable,
  preventDealSave,
  forceVendorRepull,
  setForceVendorRepull,
}: IEditProductModal) => {
  const deductiblesSelected = new Map(),
    optionSelected = new Map(),
    surchargeSelected = new Map(),
    removedProducts = new Map(),
    dealsProductsMap = new Map();
  const dispatch = useDispatch();
  const [selectedRetails, setSelectedRetails] = useState<SelectedRetails>({
    deductiblesSelected,
    optionSelected,
    surchargeSelected,
    dealsProductsMap,
    removedProducts,
  });
  const [productRules, setProductRules] = useState<ProductPlan[]>([]);
  const [dealProducts, setDealProducts] = useState<DealProduct[]>([]);
  const [changesMade, setChangesMade] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const onCloseHandler = useCallback(
    (event: object, reason?: string, closePopup = true) => {
      deductiblesSelected.clear();
      optionSelected.clear();
      surchargeSelected.clear();
      removedProducts.clear();
      setSelectedRetails({ deductiblesSelected, optionSelected, surchargeSelected, dealsProductsMap, removedProducts });

      if (changesMade) {
        setLastUpdateProductsPlanDate(new Date().toISOString());
        dispatch(setLastCommentsRefresh(new Date().toISOString()));
        selectedRetails.deductiblesSelected.clear();
        selectedRetails.dealsProductsMap.clear();
        setChangesMade(false);
      }
      closePopup && setEditProductPosition?.(0) || setForceVendorRepull?.(false);
      setRefresh(0);
    },
    [
      deductiblesSelected,
      optionSelected,
      surchargeSelected,
      removedProducts,
      customData?.type,
      setForceVendorRepull,
      selectedRetails.deductiblesSelected,
      selectedRetails.dealsProductsMap,
      changesMade,
      reloadDeal,
    ]
  );

  useEffect(() => {
    if (changesMade && refresh) {
      onCloseHandler({}, undefined, false);
    }
  }, [changesMade, refresh]);

  return (
    <Modal
      draggable
      title="Edit Products"
      dialogWidth="1250px"
      dialogHeight="80vh"
      hideBackdrop={hideBackdrop}
      open={!!editProductPosition || !!forceVendorRepull}
      resetPosition={editProductPosition}
      onClose={onCloseHandler}
    >
      <EditProductBoxWrapper>
        <ProductsTree
          getTreeUrl={customData?.getTreeUrl}
          contactAddress={contactAddress}
          type={customData?.type}
          selectedRetails={selectedRetails}
          setSelectedRetails={setSelectedRetails}
          setProductRules={setProductRules}
          dealProducts={dealProducts}
          refresh={refresh}
          preventDealSave={preventDealSave}
          forceVendorRepull={forceVendorRepull}
        />
        <EditRetailForm
          isVoidEnable={isVoidEnable}
          customData={customData}
          setSelectedRetails={setSelectedRetails}
          setChangesMade={setChangesMade}
          selectedRetails={selectedRetails}
          productRules={productRules}
          reloadDeal={reloadDeal}
          setDealProducts={setDealProducts}
          setRefresh={setRefresh}
          refresh={refresh}
          dealProducts={dealProducts}
          preventDealSave={preventDealSave}
        />
      </EditProductBoxWrapper>
    </Modal>
  );
};
